import { __extends } from "tslib";
import { AbstractScene } from "./abstractScene";
import { Mesh } from "./Meshes/mesh";
import { Logger } from './Misc/logger';
import { EngineStore } from './Engines/engineStore';
/**
 * Set of assets to keep when moving a scene into an asset container.
 */

var KeepAssets =
/** @class */
function (_super) {
  __extends(KeepAssets, _super);

  function KeepAssets() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  return KeepAssets;
}(AbstractScene);

export { KeepAssets };
/**
 * Class used to store the output of the AssetContainer.instantiateAllMeshesToScene function
 */

var InstantiatedEntries =
/** @class */
function () {
  function InstantiatedEntries() {
    /**
     * List of new root nodes (eg. nodes with no parent)
     */
    this.rootNodes = [];
    /**
     * List of new skeletons
     */

    this.skeletons = [];
    /**
     * List of new animation groups
     */

    this.animationGroups = [];
  }

  return InstantiatedEntries;
}();

export { InstantiatedEntries };
/**
 * Container with a set of assets that can be added or removed from a scene.
 */

var AssetContainer =
/** @class */
function (_super) {
  __extends(AssetContainer, _super);
  /**
   * Instantiates an AssetContainer.
   * @param scene The scene the AssetContainer belongs to.
   */


  function AssetContainer(scene) {
    var _this = _super.call(this) || this;

    _this._wasAddedToScene = false;
    _this.scene = scene;
    _this["sounds"] = [];
    _this["effectLayers"] = [];
    _this["layers"] = [];
    _this["lensFlareSystems"] = [];
    _this["proceduralTextures"] = [];
    _this["reflectionProbes"] = [];
    scene.onDisposeObservable.add(function () {
      if (!_this._wasAddedToScene) {
        _this.dispose();
      }
    });
    return _this;
  }
  /**
   * Instantiate or clone all meshes and add the new ones to the scene.
   * Skeletons and animation groups will all be cloned
   * @param nameFunction defines an optional function used to get new names for clones
   * @param cloneMaterials defines an optional boolean that defines if materials must be cloned as well (false by default)
   * @returns a list of rootNodes, skeletons and aniamtion groups that were duplicated
   */


  AssetContainer.prototype.instantiateModelsToScene = function (nameFunction, cloneMaterials) {
    var _this = this;

    if (cloneMaterials === void 0) {
      cloneMaterials = false;
    }

    var convertionMap = {};
    var storeMap = {};
    var result = new InstantiatedEntries();
    var alreadySwappedSkeletons = [];
    var alreadySwappedMaterials = [];
    var options = {
      doNotInstantiate: true
    };

    var onClone = function onClone(source, clone) {
      convertionMap[source.uniqueId] = clone.uniqueId;
      storeMap[clone.uniqueId] = clone;

      if (nameFunction) {
        clone.name = nameFunction(source.name);
      }

      if (clone instanceof Mesh) {
        var clonedMesh = clone;

        if (clonedMesh.morphTargetManager) {
          var oldMorphTargetManager = source.morphTargetManager;
          clonedMesh.morphTargetManager = oldMorphTargetManager.clone();

          for (var index = 0; index < oldMorphTargetManager.numTargets; index++) {
            var oldTarget = oldMorphTargetManager.getTarget(index);
            var newTarget = clonedMesh.morphTargetManager.getTarget(index);
            convertionMap[oldTarget.uniqueId] = newTarget.uniqueId;
            storeMap[newTarget.uniqueId] = newTarget;
          }
        }
      }
    };

    this.transformNodes.forEach(function (o) {
      if (!o.parent) {
        var newOne = o.instantiateHierarchy(null, options, function (source, clone) {
          onClone(source, clone);
        });

        if (newOne) {
          result.rootNodes.push(newOne);
        }
      }
    });
    this.meshes.forEach(function (o) {
      if (!o.parent) {
        var newOne = o.instantiateHierarchy(null, options, function (source, clone) {
          onClone(source, clone);

          if (clone.material) {
            var mesh = clone;

            if (mesh.material) {
              if (cloneMaterials) {
                var sourceMaterial = source.material;

                if (alreadySwappedMaterials.indexOf(sourceMaterial) === -1) {
                  var swap = sourceMaterial.clone(nameFunction ? nameFunction(sourceMaterial.name) : "Clone of " + sourceMaterial.name);
                  alreadySwappedMaterials.push(sourceMaterial);
                  convertionMap[sourceMaterial.uniqueId] = swap.uniqueId;
                  storeMap[swap.uniqueId] = swap;

                  if (sourceMaterial.getClassName() === "MultiMaterial") {
                    var multi = sourceMaterial;

                    for (var _i = 0, _a = multi.subMaterials; _i < _a.length; _i++) {
                      var material = _a[_i];

                      if (!material) {
                        continue;
                      }

                      swap = material.clone(nameFunction ? nameFunction(material.name) : "Clone of " + material.name);
                      alreadySwappedMaterials.push(material);
                      convertionMap[material.uniqueId] = swap.uniqueId;
                      storeMap[swap.uniqueId] = swap;
                    }
                  }
                }

                mesh.material = storeMap[convertionMap[sourceMaterial.uniqueId]];
              } else {
                if (mesh.material.getClassName() === "MultiMaterial") {
                  if (_this.scene.multiMaterials.indexOf(mesh.material) === -1) {
                    _this.scene.addMultiMaterial(mesh.material);
                  }
                } else {
                  if (_this.scene.materials.indexOf(mesh.material) === -1) {
                    _this.scene.addMaterial(mesh.material);
                  }
                }
              }
            }
          }
        });

        if (newOne) {
          result.rootNodes.push(newOne);
        }
      }
    });
    this.skeletons.forEach(function (s) {
      var clone = s.clone(nameFunction ? nameFunction(s.name) : "Clone of " + s.name);

      if (s.overrideMesh) {
        clone.overrideMesh = storeMap[convertionMap[s.overrideMesh.uniqueId]];
      }

      for (var _i = 0, _a = _this.meshes; _i < _a.length; _i++) {
        var m = _a[_i];

        if (m.skeleton === s && !m.isAnInstance) {
          var copy = storeMap[convertionMap[m.uniqueId]];
          copy.skeleton = clone;

          if (alreadySwappedSkeletons.indexOf(clone) !== -1) {
            continue;
          }

          alreadySwappedSkeletons.push(clone); // Check if bones are mesh linked

          for (var _b = 0, _c = clone.bones; _b < _c.length; _b++) {
            var bone = _c[_b];

            if (bone._linkedTransformNode) {
              bone._linkedTransformNode = storeMap[convertionMap[bone._linkedTransformNode.uniqueId]];
            }
          }
        }
      }

      result.skeletons.push(clone);
    });
    this.animationGroups.forEach(function (o) {
      var clone = o.clone(o.name, function (oldTarget) {
        var newTarget = storeMap[convertionMap[oldTarget.uniqueId]];
        return newTarget || oldTarget;
      });
      result.animationGroups.push(clone);
    });
    return result;
  };
  /**
   * Adds all the assets from the container to the scene.
   */


  AssetContainer.prototype.addAllToScene = function () {
    var _this = this;

    this._wasAddedToScene = true;
    this.cameras.forEach(function (o) {
      _this.scene.addCamera(o);
    });
    this.lights.forEach(function (o) {
      _this.scene.addLight(o);
    });
    this.meshes.forEach(function (o) {
      _this.scene.addMesh(o);
    });
    this.skeletons.forEach(function (o) {
      _this.scene.addSkeleton(o);
    });
    this.animations.forEach(function (o) {
      _this.scene.addAnimation(o);
    });
    this.animationGroups.forEach(function (o) {
      _this.scene.addAnimationGroup(o);
    });
    this.multiMaterials.forEach(function (o) {
      _this.scene.addMultiMaterial(o);
    });
    this.materials.forEach(function (o) {
      _this.scene.addMaterial(o);
    });
    this.morphTargetManagers.forEach(function (o) {
      _this.scene.addMorphTargetManager(o);
    });
    this.geometries.forEach(function (o) {
      _this.scene.addGeometry(o);
    });
    this.transformNodes.forEach(function (o) {
      _this.scene.addTransformNode(o);
    });
    this.actionManagers.forEach(function (o) {
      _this.scene.addActionManager(o);
    });
    this.textures.forEach(function (o) {
      _this.scene.addTexture(o);
    });
    this.reflectionProbes.forEach(function (o) {
      _this.scene.addReflectionProbe(o);
    });

    if (this.environmentTexture) {
      this.scene.environmentTexture = this.environmentTexture;
    }

    for (var _i = 0, _a = this.scene._serializableComponents; _i < _a.length; _i++) {
      var component = _a[_i];
      component.addFromContainer(this);
    }
  };
  /**
   * Removes all the assets in the container from the scene
   */


  AssetContainer.prototype.removeAllFromScene = function () {
    var _this = this;

    this._wasAddedToScene = false;
    this.cameras.forEach(function (o) {
      _this.scene.removeCamera(o);
    });
    this.lights.forEach(function (o) {
      _this.scene.removeLight(o);
    });
    this.meshes.forEach(function (o) {
      _this.scene.removeMesh(o);
    });
    this.skeletons.forEach(function (o) {
      _this.scene.removeSkeleton(o);
    });
    this.animations.forEach(function (o) {
      _this.scene.removeAnimation(o);
    });
    this.animationGroups.forEach(function (o) {
      _this.scene.removeAnimationGroup(o);
    });
    this.multiMaterials.forEach(function (o) {
      _this.scene.removeMultiMaterial(o);
    });
    this.materials.forEach(function (o) {
      _this.scene.removeMaterial(o);
    });
    this.morphTargetManagers.forEach(function (o) {
      _this.scene.removeMorphTargetManager(o);
    });
    this.geometries.forEach(function (o) {
      _this.scene.removeGeometry(o);
    });
    this.transformNodes.forEach(function (o) {
      _this.scene.removeTransformNode(o);
    });
    this.actionManagers.forEach(function (o) {
      _this.scene.removeActionManager(o);
    });
    this.textures.forEach(function (o) {
      _this.scene.removeTexture(o);
    });
    this.reflectionProbes.forEach(function (o) {
      _this.scene.removeReflectionProbe(o);
    });

    if (this.environmentTexture === this.scene.environmentTexture) {
      this.scene.environmentTexture = null;
    }

    for (var _i = 0, _a = this.scene._serializableComponents; _i < _a.length; _i++) {
      var component = _a[_i];
      component.removeFromContainer(this);
    }
  };
  /**
   * Disposes all the assets in the container
   */


  AssetContainer.prototype.dispose = function () {
    this.cameras.forEach(function (o) {
      o.dispose();
    });
    this.cameras = [];
    this.lights.forEach(function (o) {
      o.dispose();
    });
    this.lights = [];
    this.meshes.forEach(function (o) {
      o.dispose();
    });
    this.meshes = [];
    this.skeletons.forEach(function (o) {
      o.dispose();
    });
    this.skeletons = [];
    this.animationGroups.forEach(function (o) {
      o.dispose();
    });
    this.animationGroups = [];
    this.multiMaterials.forEach(function (o) {
      o.dispose();
    });
    this.multiMaterials = [];
    this.materials.forEach(function (o) {
      o.dispose();
    });
    this.materials = [];
    this.geometries.forEach(function (o) {
      o.dispose();
    });
    this.geometries = [];
    this.transformNodes.forEach(function (o) {
      o.dispose();
    });
    this.transformNodes = [];
    this.actionManagers.forEach(function (o) {
      o.dispose();
    });
    this.actionManagers = [];
    this.textures.forEach(function (o) {
      o.dispose();
    });
    this.textures = [];
    this.reflectionProbes.forEach(function (o) {
      o.dispose();
    });
    this.reflectionProbes = [];

    if (this.environmentTexture) {
      this.environmentTexture.dispose();
      this.environmentTexture = null;
    }

    for (var _i = 0, _a = this.scene._serializableComponents; _i < _a.length; _i++) {
      var component = _a[_i];
      component.removeFromContainer(this, true);
    }
  };

  AssetContainer.prototype._moveAssets = function (sourceAssets, targetAssets, keepAssets) {
    if (!sourceAssets) {
      return;
    }

    for (var _i = 0, sourceAssets_1 = sourceAssets; _i < sourceAssets_1.length; _i++) {
      var asset = sourceAssets_1[_i];
      var move = true;

      if (keepAssets) {
        for (var _a = 0, keepAssets_1 = keepAssets; _a < keepAssets_1.length; _a++) {
          var keepAsset = keepAssets_1[_a];

          if (asset === keepAsset) {
            move = false;
            break;
          }
        }
      }

      if (move) {
        targetAssets.push(asset);
      }
    }
  };
  /**
   * Removes all the assets contained in the scene and adds them to the container.
   * @param keepAssets Set of assets to keep in the scene. (default: empty)
   */


  AssetContainer.prototype.moveAllFromScene = function (keepAssets) {
    this._wasAddedToScene = false;

    if (keepAssets === undefined) {
      keepAssets = new KeepAssets();
    }

    for (var key in this) {
      if (this.hasOwnProperty(key)) {
        this[key] = this[key] || (key === "environmentTexture" ? null : []);

        this._moveAssets(this.scene[key], this[key], keepAssets[key]);
      }
    }

    this.removeAllFromScene();
  };
  /**
   * Adds all meshes in the asset container to a root mesh that can be used to position all the contained meshes. The root mesh is then added to the front of the meshes in the assetContainer.
   * @returns the root mesh
   */


  AssetContainer.prototype.createRootMesh = function () {
    var rootMesh = new Mesh("assetContainerRootMesh", this.scene);
    this.meshes.forEach(function (m) {
      if (!m.parent) {
        rootMesh.addChild(m);
      }
    });
    this.meshes.unshift(rootMesh);
    return rootMesh;
  };
  /**
   * Merge animations from this asset container into a scene
   * @param scene is the instance of BABYLON.Scene to append to (default: last created scene)
   * @param animatables set of animatables to retarget to a node from the scene
   * @param targetConverter defines a function used to convert animation targets from the asset container to the scene (default: search node by name)
   */


  AssetContainer.prototype.mergeAnimationsTo = function (scene, animatables, targetConverter) {
    if (scene === void 0) {
      scene = EngineStore.LastCreatedScene;
    }

    if (targetConverter === void 0) {
      targetConverter = null;
    }

    if (!scene) {
      Logger.Error("No scene available to merge animations to");
      return;
    }

    var _targetConverter = targetConverter ? targetConverter : function (target) {
      return scene.getBoneByName(target.name) || scene.getNodeByName(target.name);
    }; // Copy new node animations


    var nodesInAC = this.getNodes();
    nodesInAC.forEach(function (nodeInAC) {
      var nodeInScene = _targetConverter(nodeInAC);

      if (nodeInScene !== null) {
        var _loop_1 = function _loop_1(animationInAC) {
          // Doing treatment on an array for safety measure
          var animationsWithSameProperty = nodeInScene.animations.filter(function (animationInScene) {
            return animationInScene.targetProperty === animationInAC.targetProperty;
          });

          for (var _i = 0, animationsWithSameProperty_1 = animationsWithSameProperty; _i < animationsWithSameProperty_1.length; _i++) {
            var animationWithSameProperty = animationsWithSameProperty_1[_i];
            var index = nodeInScene.animations.indexOf(animationWithSameProperty, 0);

            if (index > -1) {
              nodeInScene.animations.splice(index, 1);
            }
          }
        }; // Remove old animations with same target property as a new one


        for (var _i = 0, _a = nodeInAC.animations; _i < _a.length; _i++) {
          var animationInAC = _a[_i];

          _loop_1(animationInAC);
        } // Append new animations


        nodeInScene.animations = nodeInScene.animations.concat(nodeInAC.animations);
      }
    }); // Copy new animation groups

    this.animationGroups.slice().forEach(function (animationGroupInAC) {
      // Clone the animation group and all its animatables
      animationGroupInAC.clone(animationGroupInAC.name, _targetConverter); // Remove animatables related to the asset container

      animationGroupInAC.animatables.forEach(function (animatable) {
        animatable.stop();
      });
    }); // Retarget animatables

    animatables.forEach(function (animatable) {
      var target = _targetConverter(animatable.target);

      if (target) {
        // Clone the animatable and retarget it
        scene.beginAnimation(target, animatable.fromFrame, animatable.toFrame, animatable.loopAnimation, animatable.speedRatio, animatable.onAnimationEnd ? animatable.onAnimationEnd : undefined, undefined, true, undefined, animatable.onAnimationLoop ? animatable.onAnimationLoop : undefined); // Stop animation for the target in the asset container

        scene.stopAnimation(animatable.target);
      }
    });
  };

  return AssetContainer;
}(AbstractScene);

export { AssetContainer };